import React from 'react';
import { setup, teardown } from 'scroll-doctor';

const contentGridStyle: React.CSSProperties = {
    gridTemplateColumns: '1fr min-content',
    gridTemplateRows: 'min-content 1fr',
};

const buttonGridStyle: React.CSSProperties = {
    gridRow: 1,
    gridColumn: 2,
};

type SearchboxDialogProps = {
    children?: React.ReactNode;
    className?: string;
    isModal: boolean;
    isOpen: boolean;
    onDismiss?: () => void;
};

export function SearchboxDialog(props: SearchboxDialogProps) {
    const { children, className = '', isModal, isOpen, onDismiss } = props;

    const isModalOpen = isModal && isOpen;

    const contentRef = React.useRef<HTMLDivElement>(null);
    const isOpenRef = React.useRef(isModalOpen);

    React.useEffect(
        function lockPageScroll() {
            teardown();
            if (!contentRef.current) return;
            isModalOpen && setup(contentRef.current);
        },
        [isModalOpen, contentRef],
    );

    const captureDialogClick = React.useCallback((ev: React.MouseEvent) => {
        if (ev.target !== ev.currentTarget) {
            return;
        }
        ev.preventDefault();
        ev.stopPropagation();
    }, []);

    React.useEffect(
        function addHistoryEntryOnOpen() {
            if (isModalOpen && !isOpenRef.current) {
                window.history.pushState({}, '');
            }
            isOpenRef.current = isModalOpen;
        },
        [isModalOpen, isOpenRef],
    );

    React.useEffect(() => {
        function dimissOnHistoryBack() {
            onDismiss && onDismiss();
        }
        window.addEventListener('popstate', dimissOnHistoryBack);
        return function cleanup() {
            window.removeEventListener('popstate', dimissOnHistoryBack);
        };
    }, [onDismiss]);

    const closeDialog = React.useCallback(() => {
        window.history.back();
    }, []);

    const closeDialogOnEscapeKey = React.useCallback(
        (ev: React.KeyboardEvent) => {
            if (ev.defaultPrevented) {
                return;
            }
            if (ev.key !== 'Escape') {
                return;
            }
            ev.preventDefault();
            ev.stopPropagation();
            closeDialog();
        },
        [closeDialog],
    );

    const modalClass = isModalOpen
        ? 'overflow-x-hidden overflow-y-auto h-full w-full fixed inset-0 z-20 bg-white'
        : '';

    const contentClass = isModalOpen
        ? 'absolute inset-0 overflow-x-hidden overflow-y-auto pt-16 px-32'
        : '';

    const gridClass = isModalOpen ? 'grid gap-x-16 searchbox-modal-grid' : '';

    const contentStyle = isModalOpen ? contentGridStyle : undefined;

    if (!isModal) {
        return <>{children}</>;
    }

    return (
        <div
            aria-label="Søk"
            className={`${className} ${modalClass}`}
            onClick={captureDialogClick}
            onKeyDown={closeDialogOnEscapeKey}
            role="dialog"
        >
            <div
                className={`${contentClass} ${gridClass}`}
                style={contentStyle}
                ref={contentRef}
            >
                {children}
                <button
                    className="button button--link"
                    hidden={!isModalOpen}
                    style={buttonGridStyle}
                    onClick={closeDialog}
                    type="reset"
                >
                    Avbryt
                </button>
            </div>
        </div>
    );
}
